import { inject, Injectable } from '@angular/core';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import {
  catchError,
  combineLatest,
  concatMap,
  filter,
  first,
  map,
  Observable,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';

import { runtimeActions, runtimeSelectors } from '@blaze-runtime/engine-data-access';
import {
  ActionTypeConfig,
  ColumnId,
  findByRefIds,
  WrapperField,
} from '@blaze-runtime/engine-domain';
import {
  ActionPageType,
  ApiActionInfo,
  PageInfo,
  PageType,
} from '@blaze-shared/api-contracts-domain';
import { fromRouter } from '@blaze-shared/app-state-data-access';
import { ActionConfiguratorFormField } from '@blaze-shared/connectors-domain';
import { DataBindingSourceInfo } from '@blaze-shared/elements-domain';
import { blazePageActions, PagesApiService } from '@blaze-shared/projects-data-access';
import { SchemaService } from '@blaze-shared/schema-data-access';
import {
  ApiCallSchema,
  ColumnQuery,
  convertApiActionInfoToApiCallSchema,
  DatasetSchema,
  getColumnKey,
  isWorkflowSchema,
  WorkflowSchema,
} from '@blaze-shared/schema-domain';
import {
  findFirstNodeInWorkflowOfType,
  hasPrompt,
  isOpenDialogNode,
} from '@blaze-shared/workflow-domain';

import { fromElements } from '../+state';
import { hasWrapperRefs } from '../canvas-elements/element-model/element-props-guards';
import { isGeneratedForm } from '../canvas-elements/element-model/element-type-guards';
import { getWrapperRefs } from '../canvas-elements/element-model/wrapper-refs';
import { LayoutsApiDataResponse, LayoutsApiService } from '../data/layouts-api.service';
import { sanitizeConnections, upsertConnections } from '../utils/runtime';

import { LayoutLoadingService } from './layout-loading.service';
import { LayoutSyncService } from './layout-sync.service';
import { PageGenerationService } from './page-pregeneration.service';
import { PregenerationUtilsService } from './pregeneration-utils.service';

export interface PopupFormGenerationData {
  shouldGenerateForm: boolean;
  workflowId: string;
  columnVisibility: Record<ColumnId | string, boolean>;
  formTitle: string | undefined;
  commitLabel: string | undefined;
  dataSource: DataBindingSourceInfo | undefined;
  dialogId?: string;
  dialogType: Exclude<ActionPageType, 'Page'>;
}

export type ActionType = 'api-call' | 'insert' | 'update-row';

const ACTION_TYPE_CONFIGS: Record<ActionType, ActionTypeConfig> = {
  'api-call': {
    getDataSource: (workflowId, schema) => ({
      sourceType: 'context',
      id: workflowId,
      columns: schema?.columns || [],
    }),
  },
  insert: {
    actionTitle: (sourceName) => {
      const titleParts = ['Add'];
      if (sourceName) {
        titleParts.push(sourceName);
      }
      return titleParts.join(' ');
    },
    commitLabel: () => 'Add',
    getDataSource: (workflowId, schema) => ({
      sourceType: 'dataset',
      id: schema?.id || '',
      columns: schema?.columns || [],
    }),
  },
  'update-row': {
    actionTitle: (sourceName) => {
      const titleParts = ['Edit'];
      if (sourceName) {
        titleParts.push(sourceName);
      }
      return titleParts.join(' ');
    },
    commitLabel: () => 'Edit',
    getDataSource: (workflowId, schema) => ({
      sourceType: 'dataset',
      id: schema?.id || '',
      columns: schema?.columns || [],
    }),
  },
};

@Injectable({
  providedIn: 'root',
})
export class ActionConfiguratorService {
  private readonly layoutLoading = inject(LayoutLoadingService);
  private readonly layoutsApi = inject(LayoutsApiService);
  private readonly layoutSyncService = inject(LayoutSyncService);
  private readonly pageGenerationService = inject(PageGenerationService);
  private readonly pagesApi = inject(PagesApiService);
  private readonly pregenerationUtilsService = inject(PregenerationUtilsService);
  private readonly schemaService = inject(SchemaService);
  private readonly store = inject(Store);

  /** @returns dialogId */
  createOrUpdatePopupForm({
    shouldGenerateForm,
    workflowId,
    columnVisibility,
    formTitle,
    commitLabel,
    dataSource,
    dialogId,
    dialogType,
  }: PopupFormGenerationData): Observable<string | undefined> {
    if (!dataSource) {
      throw new Error('Data source missing');
    }

    if (!dialogId) {
      // If dialogId not provided - try to find it in the workflow (either in node with User Input or Open Dialog node)
      this.store
        .select(runtimeSelectors.selectWorkflowById(workflowId))
        .pipe(take(1))
        .subscribe((workflow) => {
          dialogId =
            findFirstNodeInWorkflowOfType(workflow, hasPrompt)?.dialogId ||
            findFirstNodeInWorkflowOfType(workflow, isOpenDialogNode)?.dialogId;
        });
    }

    if (!shouldGenerateForm) {
      return of(undefined);
    }

    // Generate the dialog
    return this.getOrCreateDialog(dialogId, dialogType, formTitle || 'Unnamed').pipe(
      tap((dialog) => this.store.dispatch(blazePageActions.addOrUpdatePage({ page: dialog }))),
      concatLatestFrom(() => this.store.select(fromRouter.getProjectId)),
      concatMap(([dialog, projectId]) => {
        const dialogId = dialog.id;
        // Load the most up-to-date version of current layout
        return this.layoutLoading.getLayout(dialogId).pipe(
          concatMap((originalLayout) =>
            this.pageGenerationService
              .regeneratePrefilledPageLayoutMutably(
                dialogId,
                projectId,
                'prompt',
                dialog.pageType,
                dataSource,
                {
                  formTitle,
                  commitLabel,
                  keepSchema: true, // use the already known schema
                  overrideColumnVisibility: columnVisibility,
                },
                {
                  elements: originalLayout.uiLayer.content,
                  connections: originalLayout.actionLayer.connections,
                },
              )
              .pipe(
                map(({ layoutContent, connections }) => ({
                  originalLayout,
                  generatedPage: {
                    layoutContent,
                    // Preserve existing connections (upsert generated connections)
                    connections: sanitizeConnections(
                      upsertConnections(originalLayout.actionLayer.connections, connections),
                      layoutContent.map((el) => el.id),
                    ),
                  },
                })),
              ),
          ),
          concatMap(({ originalLayout, generatedPage: { layoutContent, connections } }) =>
            this.layoutsApi
              .updateLayouts([
                {
                  pageId: dialogId,
                  version: originalLayout.version,
                  uiLayer: { content: layoutContent },
                  actionLayer: { connections: connections || [] },
                },
              ])
              .pipe(
                // Side effect - update the layout & connections
                tap(([savedLayout]) => {
                  if (!savedLayout) {
                    throw new Error('Error when saving layout');
                  }
                  const mainDatasetId =
                    dataSource.sourceType === 'dataset'
                      ? dataSource.id
                      : this.schemaService.getWorkflowSchema(dataSource.id)?.tableId;
                  this.store.dispatch(fromElements.layoutFetched({ layout: savedLayout }));
                  if (mainDatasetId) {
                    this.store.dispatch(
                      fromElements.pageMainDatasetIdUpdated({ page: dialog, mainDatasetId }),
                    );
                  }
                }),
              ),
          ),
          // Emit the page id of the created/updated dialog
          map(() => dialogId),
        );
      }),
    );
  }

  /**
   * Looks for the presence of fields in the generated form on a provided page
   * @param columnKeys list of column keys (ids or names) to look for in the form
   * @param pageId the page id with the generated form
   * @returns a dictionary with values indicating whether column should be shown on form
   */
  fetchShowOnForm(
    columnKeys: ColumnQuery[] | undefined,
    pageId: string | undefined,
  ): Observable<Record<string, boolean>> {
    if (!pageId || !columnKeys || columnKeys.length === 0) {
      return of({});
    }

    // Fetch the target layout and find the actual form
    return this.layoutLoading.getLayout(pageId).pipe(
      catchError(() => {
        console.error(
          `Could not fetch layout for ${pageId} - assuming it's not existing - the user input selection might be incorrect`,
        );
        return of({ uiLayer: { content: [] } } as any as LayoutsApiDataResponse);
      }),
      map(({ uiLayer }) => {
        const generatedForm = (uiLayer.content || []).find(isGeneratedForm);
        if (!hasWrapperRefs(generatedForm)) {
          return {};
        }

        const wrapperRefs = getWrapperRefs(generatedForm);
        return columnKeys.reduce(
          (acc, idOrName) => {
            const isRefDefined = !!findByRefIds(
              wrapperRefs,
              WrapperField.getRefIds(idOrName.name, idOrName.id),
            );

            // Store the information for both the id and name, so it's matched properly regardless of which is used to match the field
            if (idOrName.id) {
              acc[idOrName.id] = isRefDefined;
            }
            if (idOrName.name) {
              acc[idOrName.name] = isRefDefined;
            }

            return acc;
          },
          {} as Record<string, boolean>,
        );
      }),
    );
  }

  preparePopupFormGenerationData(
    workflowId: string,
    action: ActionTypeConfig | undefined,
    fields: ActionConfiguratorFormField[] | undefined,
    schema: ApiCallSchema | DatasetSchema | undefined | null,
    dialogType: ActionPageType | undefined,
    dialogId?: string,
  ): PopupFormGenerationData {
    // If some fields should be shown on form - a popup form should be (re)generated
    const shouldGenerateForm =
      ((fields?.length || 0) > 0 && fields?.some((field) => field.showOnForm)) || false;

    // Column visibility
    const columnVisibility = (fields || []).reduce(
      (acc, field) => {
        acc[getColumnKey(field)] = field.showOnForm || false;
        return acc;
      },
      {} as Record<ColumnId | string, boolean>,
    );

    // Mark all extra columns as invisible
    schema?.columns?.forEach((column) => {
      if (columnVisibility[getColumnKey(column)] === undefined) {
        columnVisibility[getColumnKey(column)] = false;
      }
    });

    const formTitle = action?.actionTitle?.(schema?.name);
    const commitLabel = action?.commitLabel?.() || 'Save';
    const dataSource = action?.getDataSource?.(workflowId, schema);
    return {
      shouldGenerateForm,
      workflowId,
      columnVisibility,
      formTitle,
      commitLabel,
      dataSource,
      dialogId,
      dialogType: dialogType === 'Page' ? 'Dialog' : (dialogType ?? 'Dialog'),
    };
  }

  tryRemovePopupForm(workflowId: string | undefined, dialogId?: string): Observable<undefined> {
    return this.layoutSyncService.triggerLayoutSync().pipe(
      map((resp) => {
        if (resp) {
          this.store.dispatch(runtimeActions.tryDeleteActionDialogPage({ workflowId, dialogId }));
        }
        return undefined;
      }),
    );
  }

  getActionTypeConfig(actionType: ActionType | undefined): ActionTypeConfig | undefined {
    if (!actionType) {
      return;
    }

    return ACTION_TYPE_CONFIGS[actionType];
  }

  getSchemaFromActionType(
    actionType: ActionType | undefined,
    selectedApiActionSchema$?: Observable<ApiActionInfo | undefined>,
    selectedDatasetSchema$?: Observable<DatasetSchema | undefined>,
    input1RowSchema$?: Observable<DatasetSchema | WorkflowSchema | undefined>,
  ): Observable<ApiCallSchema | DatasetSchema | undefined> {
    switch (actionType) {
      case 'api-call':
        if (!selectedApiActionSchema$) {
          throw new Error('Selected API-action schema missing');
        }
        return selectedApiActionSchema$.pipe(
          map((info) => {
            const schema = info ? convertApiActionInfoToApiCallSchema(info) : undefined;
            return schema ? { ...schema, rawApiActionInfo: info } : undefined;
          }),
        );
      case 'insert':
        if (!selectedDatasetSchema$) {
          throw new Error('Selected dataset schema missing');
        }
        return selectedDatasetSchema$;
      case 'update-row':
        if (!input1RowSchema$) {
          throw new Error('Input1 Row Schema missing');
        }
        return input1RowSchema$.pipe(
          switchMap((rowSchema) => {
            const datasetId = isWorkflowSchema(rowSchema) ? rowSchema.tableId : rowSchema?.id;
            return datasetId
              ? this.schemaService.obtainSchema<DatasetSchema>(datasetId)
              : of(undefined);
          }),
        );
      default:
        return of(undefined);
    }
  }

  private getOrCreateDialog(
    dialogId: string | undefined,
    dialogType: PageType,
    formTitle: string,
  ): Observable<PageInfo> {
    return this.store.select(fromRouter.getProjectId).pipe(
      first(),
      concatMap((projectId) =>
        combineLatest({
          projectId: of(projectId),
          pages: this.pagesApi.getPages(projectId), // Fetch existing pages
        }),
      ),
      first(),
      concatMap(({ projectId, pages }) => {
        const matchingPage = dialogId ? pages.find((page) => page.id === dialogId) : undefined;
        if (matchingPage) {
          return of(matchingPage);
        }

        return this.pageGenerationService.createPage(
          {
            projectId,
            name: this.pregenerationUtilsService.getUniqueDialogPageName(
              `Action: ${formTitle}`,
              pages,
            ),
            pageType: dialogType,
            isHomePage: false,
            mainDatasetId: undefined,
            isActionPage: true,
          },
          pages,
        );
      }),
      filter((page) => {
        if (!page?.id) {
          throw new Error('Could not create page');
        }
        return !!page?.id;
      }),
    );
  }
}
